import type { FinanceHotspot } from '@/mocks/finance';
import { supabase } from './supabase';

type FinanceRow = {
  id: string;
  title: string;
  summary: string;
  source_name: string;
  published_at: string | null;
  fetched_at: string;
  tags: string[];
  heat_score: number | null;
  content: string | null;
  key_points: string[] | null;
  why_important: string | null;
  impact_direction: string | null;
  related_ids: string[] | null;
};

function formatDate(value: string | null): string {
  if (!value) return '时间待补充';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date).replace(/\//g, '-');
}

function heat(score: number | null): FinanceHotspot['heat'] {
  if ((score ?? 0) >= 80) return '高';
  if ((score ?? 0) >= 50) return '中';
  return '低';
}

function toHotspot(row: FinanceRow): FinanceHotspot {
  return {
    id: row.id,
    title: row.title,
    summary: row.summary,
    source: row.source_name,
    publishTime: formatDate(row.published_at),
    updateTime: formatDate(row.fetched_at),
    tags: row.tags,
    heat: heat(row.heat_score),
    impactDirection: row.impact_direction || row.tags[0] || '财经',
    content: row.content || row.summary,
    keyPoints: row.key_points?.length ? row.key_points : [row.summary],
    whyImportant: row.why_important || '这是一条由数据源抓取的财经热点，建议结合原文和后续市场信息判断。',
    relatedIds: row.related_ids || [],
  };
}

export async function loadLiveFinance(): Promise<{ items: FinanceHotspot[]; fetchedAt: Date | null } | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('finance_hotspots')
    .select('id,title,summary,source_name,published_at,fetched_at,tags,heat_score,content,key_points,why_important,impact_direction,related_ids')
    .eq('is_public', true)
    .order('heat_score', { ascending: false, nullsFirst: false })
    .order('published_at', { ascending: false, nullsFirst: false })
    .limit(30);

  if (error) throw error;
  if (!data?.length) return { items: [], fetchedAt: null };

  const rows = data as FinanceRow[];
  const fetchedAt = rows.reduce<Date | null>((latest, row) => {
    const next = new Date(row.fetched_at);
    return !latest || next > latest ? next : latest;
  }, null);

  return { items: rows.map(toHotspot), fetchedAt };
}

export async function requestFinanceRefresh(): Promise<void> {
  if (!supabase) throw new Error('Supabase 尚未配置');
  const { error } = await supabase.functions.invoke('refresh-finance');
  if (error) throw error;
}
